import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '../lib/utils.js';

export const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:shadow-[var(--shadow-focus)]',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',

      // Semantic tones. The tint formula — bg-<token>/10 with solid <token>
      // text and a transparent border — is the one apps/main already uses for
      // status badges (see StatusBadge), so these read as the same family
      // rather than a second badge vocabulary.
      neutral: 'border-transparent bg-muted text-muted-foreground',
      success: 'border-transparent bg-sla-ok/10 text-sla-ok',
      warning: 'border-transparent bg-sla-warn/10 text-sla-warn',
      info: 'border-transparent bg-primary/10 text-primary',
      /**
       * ⚠️ THE ONE TONE THAT BREAKS THE FORMULA, AND IT HAS TO.
       *
       * `bg-rush/10 text-rush` measures 3.61:1 against --bg-card and FAILS
       * WCAG AA (4.5:1 for text under 18.66px bold / 24px regular) in BOTH
       * modes — this is a badge whose whole job is small bold text. Every
       * other tone here passes; --rush is dark enough in light mode and
       * saturated enough in dark mode that it cannot carry a label on its own
       * 10%-alpha tint.
       *
       * The fix keeps the hue and moves it off the glyphs: the LABEL goes to
       * --text (10.2:1 dark, 15.9:1 light) and the tone is carried by TWO
       * non-text channels — the tint it already had, plus a solid --rush dot.
       * The dot is what stops this reading as a neutral badge, and it also
       * survives greyscale, which `text-rush` never did.
       *
       * No new hue, no new token, no size change: `before:` is a 6px pseudo
       * element inside the existing padding box.
       */
      danger:
        'border-transparent bg-rush/10 text-foreground gap-1.5 ' +
        "before:content-[''] before:h-1.5 before:w-1.5 before:shrink-0 " +
        'before:rounded-full before:bg-rush',
      },
      shape: {
        pill: 'rounded-full',
        // 5px radius — the filter-chip / status-cell shape from UX/components.md.
        chip: 'rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      shape: 'pill',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /**
   * Renders a remove button inside the pill (a removable chip). The target is
   * 24x24 (WCAG 2.2 2.5.8) and carries `removeLabel` as its accessible name,
   * e.g. "Remove Group Works". Unset renders exactly as before.
   */
  onRemove?: () => void;
  removeLabel?: string;
}

function Badge({ className, variant, shape, onRemove, removeLabel, ...props }: BadgeProps) {
  if (!onRemove) return <div className={cn(badgeVariants({ variant, shape }), className)} {...props} />;
  const { children, ...rest } = props;
  return (
    <div className={cn(badgeVariants({ variant, shape }), 'gap-1 pr-0.5', className)} {...rest}>
      {children}
      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        className="-my-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-text-dim transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
      >
        <X aria-hidden className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export { Badge };
